// src/components/studio/StudioLayout.tsx

import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Send, Heart, Image as ImageIcon, Edit } from "lucide-react";

import { ArchetypeId } from "../../constants/archetypes";
import { StepId, StepConfig } from "../../types/studio.types";

import { useStepValidation } from "../../hooks/useStepValidation";

import { canNavigateToStep } from "../../utils/stepUtils";

import { useAuth } from "../../hooks/useAuth";

import StudioFooter from "./StudioFooter";

import Step1Recipient from "./steps/Step1Recipient";
import Step2Letter from "./steps/Step2Letter";
import Step3Photo from "./steps/Step3Photo";
import Step4Gift from "./steps/Step4Gift";
import { useAppStore } from "../../store/app.store";

interface StudioLayoutProps {
  activeStep: StepId;
  setActiveStep: (step: StepId) => void;
  customization: any;
  setCustomization: (c: any) => void;
  archetype: ArchetypeId;
  onSave: () => Promise<any> | any;
  onPayment: () => void;
  createdCard: any;
  isEditing?: boolean;
}

const STEP_COMPONENTS = {
  1: Step1Recipient,
  2: Step2Letter,
  3: Step3Photo,
  4: Step4Gift,
};

const STEP_CONFIGS: StepConfig[] = [
  {
    id: 1,
    title: "Recipient",
    icon: Send,
    requiredFields: ["recipientName", "senderName"],
    validate: (customization) =>
      Boolean(customization.recipientName?.trim() && customization.senderName?.trim()),
  },
  {
    id: 2,
    title: "Letter",
    icon: Heart,
    requiredFields: ["message"],
    validate: (customization) => Boolean(customization.message?.trim().length >= 10),
  },
  {
    id: 3,
    title: "Photo",
    icon: ImageIcon,
    requiredFields: ["recipientImage"],
    validate: (customization) => Boolean(customization.recipientImage),
  },
];

// Helper function to get full-screen gradient based on archetype
const getArchetypeGradient = (archetype: ArchetypeId): string => {
  const gradients: Record<ArchetypeId, string> = {
    moments: "from-amber-400/20 via-amber-600/15 to-amber-900/30",
    heartbeat: "from-rose-400/20 via-rose-600/15 to-rose-900/30",
    safehaven: "from-blue-400/20 via-blue-600/15 to-blue-900/30",
  };
  return gradients[archetype] || "from-primary/20 via-black/20 to-black/30";
};

// Helper function to get dynamic particle color based on archetype
const getParticleColor = (archetype: ArchetypeId): string => {
  const colors: Record<ArchetypeId, string> = {
    moments: "rgba(251, 191, 36, 0.4)",
    heartbeat: "rgba(251, 113, 133, 0.4)",
    safehaven: "rgba(96, 165, 250, 0.4)",
  };
  return colors[archetype] || "rgba(255, 255, 255, 0.3)";
};

export default function StudioLayout({
  activeStep,
  setActiveStep,
  customization,
  setCustomization,
  archetype,
  onSave,
  onPayment,
  createdCard,
  isEditing = false,
}: StudioLayoutProps) {
  const navigate = useNavigate();

  const location = useLocation();

  const { isAuthenticated } = useAuth();

  const validation = useStepValidation(customization);

  const [isSaving, setIsSaving] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const setPendingAuthRedirect = useAppStore((state) => state.setPendingAuthRedirect);

  const archetypeGradient = getArchetypeGradient(archetype);
  const particleColor = getParticleColor(archetype);

  const CurrentStepComponent = STEP_COMPONENTS[activeStep as keyof typeof STEP_COMPONENTS];

  // Determine if we should show the saved card footer
  // Only show if createdCard exists AND customization has a recipientImage
  const shouldShowSavedCardFooter = Boolean(createdCard) && Boolean(customization.recipientImage);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.opacity = "1";
    }
  }, []);

  const handleNext = useCallback(async () => {
    const isValid = STEP_CONFIGS.find((s) => s.id === activeStep)?.validate(customization);

    if (!isValid && activeStep < 3) {
      return;
    }

    if (activeStep < 3) {
      setActiveStep((activeStep + 1) as StepId);
      return;
    }
    if (!isAuthenticated) {
      const returnTo = `${location.pathname}${location.search}`;

      setPendingAuthRedirect({
        returnTo,
        step: activeStep,
        reason: "finalize_surprise",
      });

      navigate("/signin", {
        state: {
          returnTo,
        },
      });

      return;
    }

    setIsSaving(true);

    try {
      await onSave();
    } finally {
      setIsSaving(false);
    }
  }, [
    activeStep,
    customization,
    isAuthenticated,
    location.pathname,
    location.search,
    navigate,
    onSave,
    setActiveStep,
  ]);

  const handleStepClick = useCallback(
    (stepId: StepId) => {
      if (canNavigateToStep(stepId, validation) && stepId !== activeStep) {
        setActiveStep(stepId);
      }
    },
    [validation, activeStep, setActiveStep]
  );

  return (
    // OUTER WRAPPER
    <div
      ref={containerRef}
      className="
  fixed
  left-0
  right-0
  bottom-0
  top-[52px]
  h-[calc(100dvh-52px)]
  w-full
  overflow-hidden
  text-white
  [scrollbar-width:none]
  [-ms-overflow-style:none]
  [&::-webkit-scrollbar]:hidden
"
      style={{ opacity: 0 }}
    >
      {/* FULLSCREEN THEMED GRADIENT BACKGROUND */}
      <div
        className={`
          fixed
          inset-0
          bg-gradient-to-br
          ${archetypeGradient}
        `}
      />

      {/* Secondary gradient overlay for depth */}
      <div
        className={`
          fixed
          inset-0
          bg-gradient-to-t
          from-black/50
          via-transparent
          to-black/20
        `}
      />

      {/* Ambient light overlay */}
      <div
        className="fixed inset-0 opacity-40"
        style={{
          background: `
            radial-gradient(circle at 20% 30%, ${particleColor}, transparent 50%),
            radial-gradient(circle at 80% 70%, ${particleColor.replace("0.4", "0.2")}, transparent 60%)
          `,
        }}
      />

      {/* Subtle vignette effect */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          boxShadow: "inset 0 0 120px rgba(0, 0, 0, 0.5)",
        }}
      />

      {/* MAIN CONTENT  */}
      <div
        className="
    relative
    z-10
h-full
    w-full
    overflow-hidden
    px-3
    py-3
    md:px-6
    md:py-8
    lg:px-10
  "
      >
        <div
          className="
    relative
    mx-auto
    flex
    h-full
    w-full
    max-w-[1500px]
    flex-col
    overflow-hidden

    rounded-[24px]
    border
    border-white/10
    bg-black/20
    backdrop-blur-[18px]
    shadow-[0_25px_80px_rgba(0,0,0,0.45)]

    md:rounded-[36px]
  "
        >
          {/* INNER GLOW */}
          <div
            className="
              absolute
              inset-0
              pointer-events-none
            "
            style={{
              background: `
                linear-gradient(
                  180deg,
                  rgba(255,255,255,0.05),
                  transparent 20%
                )
              `,
            }}
          />

          {/* Archetype-specific accent line at top */}
          <div
            className={`
              absolute
              top-0
              left-0
              right-0
              h-0.5
              bg-gradient-to-r
              from-transparent
              via-${archetype === "moments" ? "amber-400" : archetype === "heartbeat" ? "rose-400" : "blue-400"}
              to-transparent
              opacity-60
            `}
          />

          {/* EDIT BADGE */}
          {isEditing && (
            <div
              className="
                absolute
                top-20
                md:top-6
                right-8
                z-50
              "
            >
              <div
                className="
                  flex
                  items-center
                  gap-2

                  rounded-full

                  border
                  border-white/10

                  bg-black/30

                  px-3
                  py-2

                  backdrop-blur-xl
                "
              >
                <Edit size={11} className="text-primary" />

                <span
                  className="
                    text-[9px]
                    uppercase
                    tracking-[0.24em]
                    text-white/70
                  "
                >
                  Editing
                </span>
              </div>
            </div>
          )}

          {/* MOBILE STEP BAR */}
          <div
            className="
              md:hidden

              flex
              items-center
              gap-2

              overflow-x-auto

              px-4
              pt-4

              scrollbar-hide
            "
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {STEP_CONFIGS.map((step) => {
              const isActive = activeStep === step.id;

              return (
                <button
                  key={step.id}
                  onClick={() => handleStepClick(step.id)}
                  className={`
                    flex-shrink-0

                    flex
                    items-center
                    gap-2

                    h-[42px]
                    px-4

                    rounded-full

                    transition-all

                    ${isActive ? "bg-white text-black" : "bg-white/5 text-white/65"}
                  `}
                >
                  <step.icon size={13} />

                  <span
                    className="
                      text-[9px]
                      uppercase
                      tracking-[0.18em]
                    "
                  >
                    {step.title}
                  </span>
                </button>
              );
            })}
          </div>

          {/* DESKTOP LAYOUT */}
          <div
            className="
    grid
    min-h-0
    flex-1
    grid-cols-1
    overflow-hidden
    md:grid-cols-[84px_1fr]
  "
          >
            {/* DESKTOP STEP NAV */}
            <div
              className="
                hidden
                md:flex

                flex-col
                items-center
                justify-center
               

                gap-4

                py-8
                
                border-r
                border-white/6
              "
            >
              {STEP_CONFIGS.map((step) => {
                const isActive = activeStep === step.id;

                return (
                  <button
                    key={step.id}
                    onClick={() => handleStepClick(step.id)}
                    className={`
                      relative

                      w-[56px]
                      h-[56px]

                      rounded-2xl

                      transition-all
                      duration-300

                      ${
                        isActive
                          ? "bg-white text-black shadow-[0_0_30px_rgba(255,255,255,0.15)]"
                          : "bg-white/[0.04] text-white/50 hover:bg-white/[0.08]"
                      }
                    `}
                  >
                    <div
                      className="
                        absolute
                        inset-0

                        flex
                        items-center
                        justify-center
                      "
                    >
                      <step.icon size={18} />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* CONTENT */}
            <div
              className="
    flex
    min-h-0
    w-full
    flex-col
    overflow-hidden
  "
            >
              {/* HEADER */}
              <div
                className="
    flex-shrink-0
    px-4
    pt-4
    pb-2
    md:px-8
    md:pt-8
    md:pb-4
    lg:px-12
  "
              >
                <p
                  className="
                    text-[9px]
                    uppercase
                    tracking-[0.34em]
                    text-white/35
                    mb-2
                  "
                >
                  Step 0{activeStep}
                </p>

                <h1
                  className="
                    text-[22px]
                    md:text-[26px]
                    lg:text-[46px]

                    leading-[0.95]
                    tracking-[-0.06em]

                    font-light
                  "
                >
                  {STEP_CONFIGS.find((s) => s.id === activeStep)?.title}
                </h1>
              </div>

              {/* STEP CONTENT */}
              <div
                className="
    min-h-0
    flex-1
    overflow-hidden
    px-4
    md:px-8
    lg:px-12
  "
              >
                <CurrentStepComponent
                  customization={customization}
                  setCustomization={setCustomization}
                  validation={validation}
                />
              </div>

              {/* FOOTER */}
              <div
                className="
    flex-shrink-0
    px-4
    pb-[max(12px,env(safe-area-inset-bottom))]
    pt-2
    md:px-8
    md:pb-4
    lg:px-12
  "
              >
                <StudioFooter
                  activeStep={activeStep}
                  validation={validation}
                  showSavedCardFooter={shouldShowSavedCardFooter}
                  showEditingFooter={true}
                  isSaving={isSaving}
                  hasUnsavedChanges={false}
                  archetype={archetype}
                  onNext={handleNext}
                  onPreview={() => navigate(`/preview/${createdCard?.id}`)}
                  onPayment={onPayment}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
